import type { Certification, CertificationModule } from "@brookbush/types";

import clsx from "clsx";
import { memo } from "react";

import { useCertification, useCertificationModule } from "~/hooks";

type CertificationHeaderProps = {
	type: CertificationHeaderType;
	module?: CertificationModule;
	certification: Certification;
};

export enum CertificationHeaderType {
	OVERVIEW,
	RECOMMENDED_COURSE_PLAN,
	MODULE,
	ELECTIVES,
}

const CertificationHeader = ({
	certification,
	type,
}: CertificationHeaderProps) => {
	const { isLoading } = useCertification();
	const { module } = useCertificationModule();

	const isTopLevelHeader = [
		CertificationHeaderType.OVERVIEW,
		CertificationHeaderType.RECOMMENDED_COURSE_PLAN,
	].includes(type);

	const title =
		type === CertificationHeaderType.OVERVIEW
			? certification.title
			: type === CertificationHeaderType.RECOMMENDED_COURSE_PLAN
				? "Recommended Course Plan"
				: module.title;

	const moduleDescription = `To successfully complete this module, you must get ${
		module?.credits
	} credit${module?.credits > 1 ? "s" : ""} by passing courses in this module.`;
	const certificationDescription = `To successfully complete the certification, you must accumulate a total of ${certification.totalCredits} credits by passing courses across all modules. Each module offers a range of courses, each contributing a specific number of credits.`;
	const electivesDescription =
		"Elective courses are calculated automatically from extra credits earned in a category or credits earned from courses that are not part of this certification.";
	const recommendedCoursePlanDescription =
		"The following recommended course order will satisfy the credit requirements for all categories, including elective credits.";

	const description =
		type === CertificationHeaderType.OVERVIEW
			? certificationDescription
			: type === CertificationHeaderType.RECOMMENDED_COURSE_PLAN
				? recommendedCoursePlanDescription
				: type === CertificationHeaderType.ELECTIVES
					? electivesDescription
					: moduleDescription;

	const earnedCredits = isTopLevelHeader
		? certification.earnedTotalCredits
		: module.earnedCoreCredits;

	const credits = isTopLevelHeader
		? certification.totalCredits
		: module.credits;

	return (
		<div
			className={clsx("flex w-full flex-col gap-4 phablet:gap-6", {
				"animate-pulse": isLoading,
			})}
		>
			<div className="tablet:-mb-1 flex phablet:flex-row items-start phablet:items-center justify-between gap-8 phablet:gap-4">
				<div className="certification-title flex items-center gap-2">
					<h1 className="font-semibold laptop:text-3xl phablet:text-3xl text-xl xl:-mt-1">
						{title}
					</h1>

					{type === CertificationHeaderType.OVERVIEW &&
						(certification.isLegacy ? (
							<span className="whitespace-nowrap rounded-full bg-amber-100 px-3 py-0.5 font-medium text-amber-900 dark:bg-amber-300/20 dark:text-amber-300">
								Original
							</span>
						) : (
							<span className="whitespace-nowrap rounded-full bg-emerald-100 px-3 py-0.5 font-medium text-emerald-900 dark:bg-emerald-300/20 dark:text-emerald-300">
								New
							</span>
						))}
				</div>

				<div
					className={clsx(
						"mt-0.5 phablet:mt-none shrink-0 rounded-full desktop:px-4 phablet:px-3 px-3 desktop:py-1.5 phablet:py-1.5 py-1 font-semibold phablet:text-base text-sm",
						earnedCredits && earnedCredits >= credits
							? "bg-emerald-100 dark:bg-emerald-300/30 dark:text-slate-50"
							: type === CertificationHeaderType.ELECTIVES
								? "bg-purple-100 dark:bg-purple-300/30 dark:text-slate-50"
								: "bg-sky-100 dark:bg-sky-300/30 dark:text-slate-50",
					)}
				>
					<span className="mr-1">
						{earnedCredits} {earnedCredits !== undefined && " / "}
						{credits}
					</span>
					Credits
				</div>
			</div>

			{description && (
				<p className="border-sky-500 border-l-2 phablet:pl-3 pl-2 font-medium phablet:text-lg">
					<span>{description}</span>
				</p>
			)}
		</div>
	);
};

export default memo(CertificationHeader);
